import { HSComponent } from './component.js';
import { escapeHtml } from './markdown.js';
import { HELP_ENTRY_STYLES } from './help-render.js';
import { EXPLAIN_STYLES, renderEnvelope } from './explain-render.js';
import { ExplainClient } from './explain-client.js';
import styles from './explain-panel.css';
import template from './explain-panel.html';

const DEBOUNCE_MS = 300;   // precedent: the ML wizard's tune/preview loop

// Docked panel for tier-3 contextual explanations
// (docs/CONTEXTUAL_EXPLAINERS_DESIGN.md, handoff/frontend/tier3-explain-ui.md).
// Opposite lifecycle rule from tiers 1/2: nothing here is ever cached — every
// open()/updateContext() call is a fresh POST, and a response that arrives
// after the tracked configuration has moved on is discarded rather than
// rendered (the "generation" counter below), per the design's "never render
// a stale explanation" constraint.
export class HSExplainPanel extends HSComponent {
    #subject = null;
    #subjectTitle = '';
    #context = null;
    #focusStepId = null;
    #focusStepIndex = null;
    #generation = 0;
    #debounceTimer = null;

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
        this.loadStyles(HELP_ENTRY_STYLES);
        this.loadStyles(EXPLAIN_STYLES);
        this.onEvent('click', () => this.toggle(false), this.shadowRoot.querySelector('.ep-close-btn'));
        this.onEvent('click', () => this.#recomputeNow(), this.shadowRoot.querySelector('.ep-refresh-btn'));
        this.onEvent('click', (_, ev) => {
            const link = ev.target.closest('[data-explain-nav]');
            if (link) { this.#handleNav(link.dataset.explainNav); return; }
            const scrollBtn = ev.target.closest('[data-explain-scroll-step]');
            if (scrollBtn) this.#scrollToStep(scrollBtn.dataset.explainScrollStep);
        }, this.shadowRoot.querySelector('.ep-body'));
    }

    get isOpen() {
        return this.shadowRoot.querySelector('.ep-drawer')?.classList.contains('open') ?? false;
    }

    toggle(force) {
        const drawer = this.shadowRoot.querySelector('.ep-drawer');
        const willOpen = typeof force === 'boolean' ? force : !drawer.classList.contains('open');
        drawer.classList.toggle('open', willOpen);
        if (!willOpen) {
            clearTimeout(this.#debounceTimer);
            this.#generation++;   // invalidate any response still in flight
        }
    }

    // Open (or re-open) the panel for a subject+context and compute
    // immediately — no debounce on the explicit open, only on tracked edits.
    open({ subject, subjectTitle, context, focusStepId = null, focusStepIndex = null }) {
        this.#subject = subject;
        this.#subjectTitle = subjectTitle || subject;
        this.#context = context;
        this.#focusStepId = focusStepId;
        this.#focusStepIndex = focusStepIndex;
        this.toggle(true);
        clearTimeout(this.#debounceTimer);
        this.#recomputeNow();
    }

    // Live recomputation (task 5) — call on every relevant configuration
    // change while this panel might be open. No-ops if closed or not yet
    // tracking a subject, so callers can call this unconditionally.
    updateContext(context, { focusStepId, focusStepIndex } = {}) {
        if (!this.isOpen || !this.#subject) return;
        this.#context = context;
        if (focusStepId !== undefined) this.#focusStepId = focusStepId;
        if (focusStepIndex !== undefined) this.#focusStepIndex = focusStepIndex;
        clearTimeout(this.#debounceTimer);
        this.#debounceTimer = setTimeout(() => this.#recomputeNow(), DEBOUNCE_MS);
    }

    async #recomputeNow() {
        if (!this.#subject) return;
        const generation = ++this.#generation;
        this.#renderStatus('computing…');
        this.shadowRoot.querySelector('.ep-title').textContent = this.#subjectTitle;
        try {
            const envelope = await ExplainClient.explain(this.#subject, this.#context);
            if (generation !== this.#generation) return;   // config moved on — discard
            this.#renderStatus('');
            this.shadowRoot.querySelector('.ep-body').innerHTML = renderEnvelope(envelope, { focusStepId: this.#focusStepId, focusStepIndex: this.#focusStepIndex });
            this.shadowRoot.querySelector('.ex-step-focused')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } catch (err) {
            if (generation !== this.#generation) return;
            this.#renderStatus('');
            this.shadowRoot.querySelector('.ep-body').innerHTML =
                `<div class="ep-status ep-status-error">Could not compute explanation${err?.message ? `: ${escapeHtml(err.message)}` : ''}.</div>`;
        }
    }

    #renderStatus(text) {
        this.shadowRoot.querySelector('.ep-status-badge').textContent = text;
        if (text) this.shadowRoot.querySelector('.ep-body').innerHTML = `<div class="ep-status">${escapeHtml(text)}</div>`;
    }

    // Findings-index entries (top-level findings, rendered as a scroll-to
    // list rather than duplicating the step's own inline finding — see
    // explain-render.js#renderFindingsIndex) jump to and briefly highlight
    // the owning step. A finding with no step_id (pipeline-level only) is a
    // silent no-op, same "skip rather than break" rule as everywhere else.
    #scrollToStep(stepId) {
        if (!stepId) return;
        const el = this.shadowRoot.querySelector(`.ex-step[data-step-id="${CSS.escape(stepId)}"]`);
        if (!el) return;
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        el.classList.add('ex-step-focused');
        setTimeout(() => el.classList.remove('ex-step-focused'), 1500);
    }

    // concepts[]/help_keys[] reuse the existing tier-1/tier-2 navigation
    // rather than a second lookup — the caller (pipeline-sandbox.js) owns
    // the mounted <hs-concept-panel>/<hs-help-modal> and wires this event.
    #handleNav(spec) {
        const sep = spec.indexOf(':');
        if (sep < 0) return;
        const kind = spec.slice(0, sep);
        const key = spec.slice(sep + 1);
        if (!key) return;
        this.emit({ type: 'hs-explain-nav', kind, key });
    }
}

customElements.define('hs-explain-panel', HSExplainPanel);
